import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

let gradientCounter = 0;

@Component({
  selector: 'app-sparkline',
  imports: [TranslatePipe],
  templateUrl: './sparkline.component.html',
  styleUrl: './sparkline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparklineComponent {
  data = input.required<number[]>();
  colorVar = input.required<string>();
  width = input<number>(200);
  height = input<number>(36);
  ariaLabel = input<string>('');

  protected readonly gradientId = `to-sparkline-grad-${gradientCounter++}`;

  protected readonly hasData = computed(() => this.data().length >= 2);

  protected readonly strokeColor = computed(() => `var(${this.colorVar()})`);

  protected readonly trendKey = computed(() => {
    const data = this.data();
    const delta = data[data.length - 1] - data[0];
    if (delta > 0) return 'liveDashboard.sparkline.trendUp';
    if (delta < 0) return 'liveDashboard.sparkline.trendDown';
    return 'liveDashboard.sparkline.trendFlat';
  });

  private readonly geometry = computed(() => {
    const data = this.data();
    const width = this.width();
    const height = this.height();
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const step = width / (data.length - 1);
    const coords = data.map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / range) * (height - 4) - 2;
      return { x, y };
    });
    return { coords, lastY: coords[coords.length - 1].y };
  });

  protected readonly strokePoints = computed(() =>
    this.geometry()
      .coords.map((point) => `${this.round(point.x)},${this.round(point.y)}`)
      .join(' '),
  );

  protected readonly fillPoints = computed(
    () => `0,${this.height()} ${this.strokePoints()} ${this.width()},${this.height()}`,
  );

  protected readonly lastY = computed(() => this.round(this.geometry().lastY));

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
